import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ShieldCheck, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { CAPS } from '@/lib/kbCaps';
import { extractKbFieldErrors, type FieldErrorMap } from '@/lib/kbFieldErrors';
import { FieldError } from '@/components/onboarding/enrichment/FieldError';
import { CharCounter } from '@/components/shared/CharCounter';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { cn } from '@/lib/utils';

const MAX_SCOPE = CAPS.forbidden_scope_items;

interface LimitsEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialForbiddenScope: string[];
  initialDisclaimer: string | null;
  initialNeverSay: string | null;
  initialReferExternalDestination: string | null;
  initialForbiddenScopeNotify: boolean;
}

export function LimitsEditor({
  open,
  onOpenChange,
  initialForbiddenScope,
  initialDisclaimer,
  initialNeverSay,
  initialReferExternalDestination,
  initialForbiddenScopeNotify,
}: LimitsEditorProps) {
  const { t } = useTranslation();
  const { updateKbProfile } = useOnboardingState();
  const [scopeList, setScopeList] = useState<string[]>(initialForbiddenScope);
  const [scopeInput, setScopeInput] = useState('');
  const [forbiddenScopeNotify, setForbiddenScopeNotify] = useState(initialForbiddenScopeNotify);
  const [disclaimer, setDisclaimer] = useState(initialDisclaimer ?? '');
  const [neverSay, setNeverSay] = useState(initialNeverSay ?? '');
  const [referExternal, setReferExternal] = useState(initialReferExternalDestination ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap | null>(null);

  function addScope() {
    const trimmed = scopeInput.trim();
    if (!trimmed || scopeList.length >= MAX_SCOPE) return;
    setScopeList((prev) => [...prev, trimmed]);
    setScopeInput('');
  }

  function removeScope(index: number) {
    setScopeList((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setFieldErrors(null);
    try {
      await updateKbProfile({
        forbidden_scope: scopeList,
        forbidden_scope_notify: forbiddenScopeNotify,
        persistent_disclaimer: disclaimer || null,
        never_say: neverSay || null,
        refer_external_destination: referExternal || null,
      });
      onOpenChange(false);
    } catch (err) {
      const fe = extractKbFieldErrors(err);
      if (fe) {
        setFieldErrors(fe);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('bot.enrich.limits.title')}</DialogTitle>
          <DialogDescription>{t('bot.enrich.limits.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 max-h-[min(60dvh,calc(100dvh-16rem))] overflow-y-auto pr-1">
          {/* Forbidden scope phrases */}
          <div className="space-y-2">
            <Label>{t('bot.enrich.limits.forbidden_scope_label')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('bot.enrich.limits.forbidden_scope_help')}
            </p>
            <div className="flex gap-2">
              <Input
                value={scopeInput}
                onChange={(e) => setScopeInput(e.target.value)}
                placeholder={t('bot.enrich.limits.forbidden_scope_placeholder')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addScope();
                  }
                }}
                maxLength={CAPS.forbidden_scope}
                disabled={scopeList.length >= MAX_SCOPE}
                className="min-h-11"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={addScope}
                disabled={scopeList.length >= MAX_SCOPE || !scopeInput.trim()}
                aria-label={t('bot.enrich.limits.add')}
                className="min-h-11 min-w-11"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            {scopeList.length > 0 && (
              <ul className="space-y-1">
                {scopeList.map((phrase, idx) => (
                  <li
                    key={idx}
                    className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate">{phrase}</span>
                    <button
                      type="button"
                      onClick={() => removeScope(idx)}
                      aria-label={t('bot.enrich.limits.remove')}
                      className={cn(
                        'shrink-0 text-muted-foreground hover:text-foreground transition-colors',
                        HIT_AREA_44
                      )}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-3">
              <Label
                htmlFor="limits-forbidden-scope-notify"
                className="min-w-0 text-sm font-normal"
              >
                {t('bot.enrich.limits.forbidden_scope_notify_label')}
              </Label>
              <Switch
                id="limits-forbidden-scope-notify"
                checked={forbiddenScopeNotify}
                onCheckedChange={setForbiddenScopeNotify}
                aria-label={t('bot.enrich.limits.forbidden_scope_notify_label')}
              />
            </div>
          </div>

          {/* Persistent disclaimer */}
          <div className="space-y-2">
            <Label htmlFor="limits-disclaimer">{t('bot.enrich.limits.disclaimer_label')}</Label>
            <Textarea
              id="limits-disclaimer"
              value={disclaimer}
              onChange={(e) => setDisclaimer(e.target.value)}
              placeholder={t('bot.enrich.limits.disclaimer_placeholder')}
              rows={3}
              maxLength={CAPS.persistent_disclaimer}
            />
            <CharCounter value={disclaimer} max={CAPS.persistent_disclaimer} />
            <FieldError errors={fieldErrors} path="persistent_disclaimer" />
          </div>

          {/* F168 FR3: never_say — moved in from the Voice card, literal-phrase blocklist */}
          <div className="space-y-2">
            <Label htmlFor="limits-never-say">{t('bot.enrich.limits.never_say_label')}</Label>
            <p className="text-xs text-muted-foreground">{t('bot.enrich.limits.never_say_help')}</p>
            <Textarea
              id="limits-never-say"
              value={neverSay}
              onChange={(e) => setNeverSay(e.target.value)}
              placeholder={t('bot.enrich.limits.never_say_placeholder')}
              rows={3}
              maxLength={CAPS.never_say}
            />
            <CharCounter value={neverSay} max={CAPS.never_say} />
            <FieldError errors={fieldErrors} path="never_say" />
          </div>

          {/* F168 FR4: crisis routing destination */}
          <div className="space-y-2">
            <Label htmlFor="limits-refer-external">
              {t('bot.enrich.limits.refer_external_label')}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t('bot.enrich.limits.refer_external_help')}
            </p>
            <Input
              id="limits-refer-external"
              value={referExternal}
              onChange={(e) => setReferExternal(e.target.value)}
              placeholder={t('bot.enrich.limits.refer_external_placeholder')}
              maxLength={CAPS.refer_external_destination}
              className="min-h-11"
            />
            <FieldError errors={fieldErrors} path="refer_external_destination" />
          </div>

          {/* F168 FR2: read-only "always on" safety floor — no toggles, nothing to configure */}
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
              <span>{t('bot.enrich.limits.always_on_title')}</span>
            </div>
            <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              <li>{t('bot.enrich.limits.always_on_improper')}</li>
              <li>{t('bot.enrich.limits.always_on_regulated')}</li>
              <li>{t('bot.enrich.limits.always_on_crisis')}</li>
            </ul>
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="min-h-11"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="min-h-11">
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
